export const DAILY_KPI_BRANCHES = [
  {
    dailyBranchId: 0,
    seasonBranchId: 1,
    location: "danang",
    name: "Đà Nẵng",
  },
  {
    dailyBranchId: 1,
    seasonBranchId: 2,
    location: "hcm",
    name: "Hồ Chí Minh",
  },
  {
    dailyBranchId: 2,
    seasonBranchId: 3,
    location: "bonpas",
    name: "Bonpas",
  },
];

export const KPI_TIME_ZONE = "Asia/Ho_Chi_Minh";
export const KPI_REQUIRED_SOURCE = "frontend-dynamic";
const VIETNAM_UTC_OFFSET_HOURS = 7;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const formatDateKeyInVietnam = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: KPI_TIME_ZONE,
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return values.year && values.month && values.day
    ? `${values.year}-${values.month}-${values.day}`
    : "";
};

export const getVietnamDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  return formatDateKeyInVietnam(date);
};

export const getMillisecondsUntilNextVietnamDay = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return ONE_DAY_MS;

  const todayKey = getVietnamDateKey(date);
  const [year, month, day] = todayKey.split("-").map(Number);
  if (!year || !month || !day) return ONE_DAY_MS;

  const nextMidnightUtc =
    Date.UTC(year, month - 1, day + 1) -
    VIETNAM_UTC_OFFSET_HOURS * 60 * 60 * 1000;
  return Math.max(0, nextMidnightUtc - date.getTime());
};

export const normalizeDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  return getVietnamDateKey(value);
};

export const formatDateLabel = (value, includeWeekday = true) => {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return "Không xác định";

  const date = new Date(`${dateKey}T12:00:00+07:00`);
  return new Intl.DateTimeFormat("vi-VN", {
    weekday: includeWeekday ? "long" : undefined,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: KPI_TIME_ZONE,
  }).format(date);
};

export const generateDateKeys = (startDate, endDate) => {
  const startKey = normalizeDateKey(startDate);
  const endKey = normalizeDateKey(endDate);
  if (!startKey || !endKey || startKey > endKey) return [];

  const dates = [];
  let currentKey = startKey;

  while (currentKey <= endKey) {
    dates.push(currentKey);
    currentKey = getNextDateKey(currentKey);
  }

  return dates;
};

const getNextDateKey = (dateKey) => {
  const normalized = normalizeDateKey(dateKey);
  if (!normalized) return "";

  const date = new Date(`${normalized}T12:00:00+07:00`);
  return getVietnamDateKey(new Date(date.getTime() + ONE_DAY_MS));
};

export const groupDailyKpiRanges = (dailyKpiRows, branchId) => {
  if (branchId === null || branchId === undefined || branchId === "") return [];

  const rowsByDate = new Map(
    (Array.isArray(dailyKpiRows) ? dailyKpiRows : [])
      .filter(
        (item) =>
          Number(item.ChiNhanh) === Number(branchId) &&
          normalizeDateKey(item.Ngay)
      )
      .map((item) => [normalizeDateKey(item.Ngay), item])
  );
  const sortedRows = Array.from(rowsByDate.entries())
    .sort(([firstDate], [secondDate]) => firstDate.localeCompare(secondDate))
    .map(([dateKey, item]) => ({ ...item, Ngay: dateKey }));

  return sortedRows.reduce((ranges, row) => {
    const currentRange = ranges[ranges.length - 1];
    if (
      !currentRange ||
      getNextDateKey(currentRange.endDate) !== row.Ngay
    ) {
      ranges.push({
        key: `${row.Ngay}:${row.Ngay}`,
        startDate: row.Ngay,
        endDate: row.Ngay,
        rows: [row],
      });
      return ranges;
    }

    currentRange.endDate = row.Ngay;
    currentRange.key = `${currentRange.startDate}:${row.Ngay}`;
    currentRange.rows.push(row);
    return ranges;
  }, []);
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const omitApiRequiredKpi = (item) =>
  Object.fromEntries(
    Object.entries(item || {}).filter(([field]) => field !== "KPI_ThucSu")
  );

export const normalizeDailyKpiResponse = (payload) => {
  const groups = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return groups.flatMap((group) => {
    const branchId = Number(group.ChiNhanh);
    const rows = Array.isArray(group.Data) ? group.Data : [];

    return rows
      .map((rawItem) => {
        const item = omitApiRequiredKpi(rawItem);
        return {
          ...item,
          ChiNhanh: Number.isFinite(Number(item.ChiNhanh))
            ? Number(item.ChiNhanh)
            : branchId,
          Ngay: normalizeDateKey(item.Ngay),
          NgayBatDauPhase: normalizeDateKey(item.NgayBatDauPhase),
          NgayKetThucPhase: normalizeDateKey(item.NgayKetThucPhase),
          KPI_Goc: toNumber(item.KPI_Goc),
          ThucTe: toNumber(item.ThucTe),
        };
      })
      .filter((item) => Number.isFinite(item.ChiNhanh) && item.Ngay);
  });
};

export const calculateRequiredKpi = (
  rows,
  today = getVietnamDateKey()
) => {
  const required = rows.map((row) => Math.max(0, toNumber(row.originalKpi)));
  const original = [...required];
  const todayKey = normalizeDateKey(today);

  rows.forEach((row, index) => {
    if (!todayKey || row.date >= todayKey) return;

    const difference = toNumber(row.actual) - required[index];
    if (difference === 0) return;

    const remainingOriginal = original
      .slice(index + 1)
      .reduce((sum, value) => sum + value, 0);
    if (remainingOriginal <= 0) return;

    for (let nextIndex = index + 1; nextIndex < rows.length; nextIndex += 1) {
      const ratio = original[nextIndex] / remainingOriginal;
      required[nextIndex] = Math.max(
        0,
        required[nextIndex] - Math.round(difference * ratio)
      );
    }
  });

  return required;
};

export const buildBranchDailyKpiRows = ({
  branch,
  dailyKpiRows,
  actualByDate,
  startDate,
  endDate,
  todayKey,
}) => {
  const dailyKpiByDate = new Map(
    dailyKpiRows
      .filter((item) => Number(item.ChiNhanh) === branch.seasonBranchId)
      .map((item) => [normalizeDateKey(item.Ngay), item])
  );

  const rows = generateDateKeys(startDate, endDate).map((date) => {
    const savedKpi = dailyKpiByDate.get(date);

    return {
      date,
      originalKpi: Math.max(0, toNumber(savedKpi?.KPI_Goc)),
      actual: Math.max(0, toNumber(actualByDate.get(date))),
    };
  });

  // KPI_ThucSu từ API không được trộn vào đây: cả hai chart dùng duy nhất
  // KPI_Goc + thực tế dashboard ledger để tính KPI cần đạt động ở frontend.
  const requiredKpi = calculateRequiredKpi(rows, todayKey);
  return rows.map((row, index) => ({
    ...row,
    requiredKpi: requiredKpi[index],
  }));
};
